import { QueryClientProvider } from '@tanstack/react-query';
import type * as React from 'react';
import { BrowserRouter } from 'react-router-dom';

import { AuthorizationProvider } from '@/features/access';
import { AuthProvider } from '@/features/auth';
import { DemoBadge } from '@/features/demo';
import { queryClient } from '@/shared/api';
import { ErrorBoundary } from '@/shared/components/feedback/error-boundary';
import { TooltipProvider } from '@/shared/components/ui/tooltip';
import { Toaster } from '@/shared/components/ui/toaster';
import { ThemeProvider } from '@/shared/hooks/use-theme';

/**
 * The provider stack, ordered by dependency.
 *
 * Router sits above the auth providers because guards navigate; auth sits above
 * authorization because `/users/me/access` is only fetched once a session
 * exists. `ErrorBoundary` is outermost so a crash inside any provider still
 * renders a recoverable screen.
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <AuthorizationProvider>
                <TooltipProvider delayDuration={200}>
                  {children}
                  <DemoBadge />
                  <Toaster />
                </TooltipProvider>
              </AuthorizationProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
